import { Injectable } from "@angular/core";
import { Action } from "@ngrx/store";
import { ActionWithPayload, createCachedGetAction, SYNC_SUFFIX } from "@obg/ngrx-utils";

import { AppError } from "../app-error/app-error.model";
import { LoginRequest } from "../auth/session.model";

import {
	ConfirmCogaCustomerProfileRequest,
	ConfirmPegaCustomerProfileRequest,
	Customer,
	CustomerCredentialType,
	CustomerDetailSection,
	EmailAvailability,
} from "./customer-management.model";

export const ACTION_PREFIX = "[Customer Management]";
export const CUSTOMER_MANAGEMENT_ACTION_TYPE = {
	getCustomer: `${ACTION_PREFIX} Get`,
	getCustomerSuccess: `${ACTION_PREFIX} Get Success`,
	getCustomerFail: `${ACTION_PREFIX} Get Fail`,

	getCustomerDetail: `${ACTION_PREFIX} Get Detail`,
	getCustomerDetailSuccess: `${ACTION_PREFIX} Get Detail Success`,
	getCustomerDetailFail: `${ACTION_PREFIX} Get Detail Fail`,

	updateCustomer: `${ACTION_PREFIX} Update`,
	updateCustomerSuccess: `${ACTION_PREFIX} Update Success`,
	updateCustomerFail: `${ACTION_PREFIX} Update Fail`,

	updateCustomerDetail: `${ACTION_PREFIX} Update Detail`,
	updateCustomerDetailSuccess: `${ACTION_PREFIX} Update Detail Success`,
	updateCustomerDetailFail: `${ACTION_PREFIX} Update Detail Fail`,

	updateCustomerSubscriptions: `${ACTION_PREFIX} Update Customer Subscriptions ${SYNC_SUFFIX}`,

	updateCredentials: `${ACTION_PREFIX} Update Credentials ${SYNC_SUFFIX}`,
	deleteCredentials: `${ACTION_PREFIX} Delete Credentials ${SYNC_SUFFIX}`,

	updateCustomerCredentialTypes: `${ACTION_PREFIX} Update Customer Credential Types ${SYNC_SUFFIX}`,

	verifyToken: `${ACTION_PREFIX} Verify Token`,
	verifyTokenSuccess: `${ACTION_PREFIX} Verify Token Success`,
	verifyTokenFail: `${ACTION_PREFIX} Verify Token Fail`,

	checkEmailAvailability: `${ACTION_PREFIX} Check Email Availability`,
	checkEmailAvailabilitySuccess: `${ACTION_PREFIX} Check Email Availability Success`,
	checkEmailAvailabilityFail: `${ACTION_PREFIX} Check Email Availability Fail`,

	unsubscribeFromNewsletter: `${ACTION_PREFIX} Unsubscribe From Newsletter`,
	unsubscribeFromNewsletterSuccess: `${ACTION_PREFIX} Unsubscribe From Newsletter Success`,
	unsubscribeFromNewsletterFail: `${ACTION_PREFIX} Unsubscribe From Newsletter Fail`,

	acceptTermsAndConditions: `${ACTION_PREFIX} Accept Terms And Conditions`,
	acceptLatestTermsAndConditions: `${ACTION_PREFIX} Accept Latest Terms And Conditions`,
	acceptTermsAndConditionsSuccess: `${ACTION_PREFIX} Accept Terms And Conditions Success`,
	acceptTermsAndConditionsFail: `${ACTION_PREFIX} Accept Terms And Conditions Fail`,
	dismissAcceptTermsAndConditions: `${ACTION_PREFIX} Accept Terms And Conditions Fail`,
	clearDismissedTermsAndConditions: `${ACTION_PREFIX} Clear Dismissed Terms And Conditions `,

	confirmProfile: `${ACTION_PREFIX} Confirm Profile`,
	confirmProfileSuccess: `${ACTION_PREFIX} Confirm Profile Success`,
	confirmProfileFail: `${ACTION_PREFIX} Confirm Profile Fail`,

	declineTermsAndConditions: `${ACTION_PREFIX} Decline Terms And Conditions`,
	declineTermsAndConditionsSuccess: `${ACTION_PREFIX} Decline Terms And Conditions Success`,
	declineTermsAndConditionsFail: `${ACTION_PREFIX} Decline Terms And Conditions Fail`,

	activateDepositTermsAndConditions: `${ACTION_PREFIX} Activate Deposit Terms And Conditions ${SYNC_SUFFIX}`,
	cancelDepositTermsAndConditions: `${ACTION_PREFIX} Cancel Deposit Terms And Conditions ${SYNC_SUFFIX}`,
	acceptDepositTermsAndConditions: `${ACTION_PREFIX} Accept Deposit Terms And Conditions`,
	acceptDepositTermsAndConditionsSuccess: `${ACTION_PREFIX} Accept Deposit Terms And Conditions Success`,
	acceptDepositTermsAndConditionsFail: `${ACTION_PREFIX} Accept Deposit Terms And Conditions Fail`,

	clearCustomer: `${ACTION_PREFIX} Clear ${SYNC_SUFFIX}`,

	latestTermsAndConditionsNotAccepted: `${ACTION_PREFIX} Terms And Conditions Not Accepted ${SYNC_SUFFIX}`,
	clearAcceptedLatestTermsAndConditions: `${ACTION_PREFIX} Clear Accepted Latest Terms And Conditions ${SYNC_SUFFIX}`,

	latestPrivacyPolicyNotAccepted: `${ACTION_PREFIX} Latest Privacy Policy Not Accepted ${SYNC_SUFFIX}`,
	entityLegalNoticeNotAccepted: `${ACTION_PREFIX} Entity Legal Notice Not Accepted ${SYNC_SUFFIX}`,
	clearAcceptedLatestPrivacyPolicy: `${ACTION_PREFIX} Clear Accepted Latest Privacy Policy ${SYNC_SUFFIX}`,

	acceptPrivacyPolicy: `${ACTION_PREFIX} Accept latest Privacy Policy ${SYNC_SUFFIX}`,
	acceptPrivacyPolicySuccess: `${ACTION_PREFIX} Accept latest Privacy Policy Success`,
	acceptPrivacyPolicyFail: `${ACTION_PREFIX} Decline latest Privacy Policy Fail`,

	acceptEntityLegalNotice: `${ACTION_PREFIX} Accept Entity Legal Notice`,
	acceptEntityLegalNoticeSuccess: `${ACTION_PREFIX} Accept Entity Legal Notice Success`,
	acceptEntityLegalNoticeFail: `${ACTION_PREFIX} Accept Entity Legal Notice Fail`,

	updatePersistentData: `${ACTION_PREFIX} Update Persistent Data ${SYNC_SUFFIX}`,

	updateProfileValidation: `${ACTION_PREFIX} Update Profile Validation`,
	updateProfileValidationSuccess: `${ACTION_PREFIX} Update Profile Validation Success`,
	updateProfileValidationFail: `${ACTION_PREFIX} Update Profile Validation Fail`,
};

@Injectable({
	providedIn: "root",
})
export class CustomerManagementAction {
	getCustomer(): Action {
		return createCachedGetAction({
			type: CUSTOMER_MANAGEMENT_ACTION_TYPE.getCustomer,
		});
	}

	getCustomerSuccess(payload: Customer): ActionWithPayload {
		return {
			type: CUSTOMER_MANAGEMENT_ACTION_TYPE.getCustomerSuccess,
			payload,
		};
	}

	getCustomerFail(payload: AppError): ActionWithPayload {
		return {
			type: CUSTOMER_MANAGEMENT_ACTION_TYPE.getCustomerFail,
			payload,
		};
	}

	getCustomerDetail(payload?: CustomerDetailSection): ActionWithPayload {
		return {
			type: CUSTOMER_MANAGEMENT_ACTION_TYPE.getCustomerDetail,
			payload,
		};
	}

	getCustomerDetailSuccess(payload: Partial<Customer>): ActionWithPayload {
		return {
			type: CUSTOMER_MANAGEMENT_ACTION_TYPE.getCustomerDetailSuccess,
			payload,
		};
	}

	getCustomerDetailFail(payload: AppError): ActionWithPayload {
		return {
			type: CUSTOMER_MANAGEMENT_ACTION_TYPE.getCustomerDetailFail,
			payload,
		};
	}

	updateCustomer(payload: Partial<Customer>): ActionWithPayload {
		return {
			type: CUSTOMER_MANAGEMENT_ACTION_TYPE.updateCustomer,
			payload,
		};
	}

	updateCustomerSuccess(payload: Customer): ActionWithPayload {
		return {
			type: CUSTOMER_MANAGEMENT_ACTION_TYPE.updateCustomerSuccess,
			payload,
		};
	}

	updateCustomerFail(payload: AppError): ActionWithPayload {
		return {
			type: CUSTOMER_MANAGEMENT_ACTION_TYPE.updateCustomerFail,
			payload,
		};
	}

	updateCustomerSubscriptions(payload: Partial<Customer>): ActionWithPayload {
		return {
			type: CUSTOMER_MANAGEMENT_ACTION_TYPE.updateCustomerSubscriptions,
			payload,
		};
	}

	verifyToken(token: string): ActionWithPayload {
		return {
			type: CUSTOMER_MANAGEMENT_ACTION_TYPE.verifyToken,
			payload: token,
		};
	}

	verifyTokenSuccess(): Action {
		return {
			type: CUSTOMER_MANAGEMENT_ACTION_TYPE.verifyTokenSuccess,
		};
	}

	verifyTokenFail(payload: AppError): ActionWithPayload {
		return {
			type: CUSTOMER_MANAGEMENT_ACTION_TYPE.verifyTokenFail,
			payload,
		};
	}

	checkEmailAvailability(email: string): ActionWithPayload {
		return {
			type: CUSTOMER_MANAGEMENT_ACTION_TYPE.checkEmailAvailability,
			payload: email,
		};
	}

	checkEmailAvailabilitySuccess(payload: EmailAvailability): ActionWithPayload {
		return {
			type: CUSTOMER_MANAGEMENT_ACTION_TYPE.checkEmailAvailabilitySuccess,
			payload,
		};
	}

	checkEmailAvailabilityFail(payload: AppError): ActionWithPayload {
		return {
			type: CUSTOMER_MANAGEMENT_ACTION_TYPE.checkEmailAvailabilityFail,
			payload,
		};
	}

	unsubscribeFromNewsletter(token: string): ActionWithPayload {
		return {
			type: CUSTOMER_MANAGEMENT_ACTION_TYPE.unsubscribeFromNewsletter,
			payload: token,
		};
	}

	unsubscribeFromNewsletterSuccess(): Action {
		return {
			type: CUSTOMER_MANAGEMENT_ACTION_TYPE.unsubscribeFromNewsletterSuccess,
		};
	}

	unsubscribeFromNewsletterFail(payload: AppError): ActionWithPayload {
		return {
			type: CUSTOMER_MANAGEMENT_ACTION_TYPE.unsubscribeFromNewsletterFail,
			payload,
		};
	}

	latestTermsAndConditionsNotAccepted(payload: AppError): ActionWithPayload {
		return {
			type: CUSTOMER_MANAGEMENT_ACTION_TYPE.latestTermsAndConditionsNotAccepted,
			payload,
		};
	}

	acceptTermsAndConditions(token: string): ActionWithPayload {
		return {
			type: CUSTOMER_MANAGEMENT_ACTION_TYPE.acceptTermsAndConditions,
			payload: token,
		};
	}

	acceptLatestTermsAndConditions(token: string): ActionWithPayload {
		return {
			type: CUSTOMER_MANAGEMENT_ACTION_TYPE.acceptLatestTermsAndConditions,
			payload: token,
		};
	}

	acceptTermsAndConditionsSuccess(): Action {
		return {
			type: CUSTOMER_MANAGEMENT_ACTION_TYPE.acceptTermsAndConditionsSuccess,
		};
	}

	clearAcceptedLatestTermsAndConditions(): Action {
		return {
			type: CUSTOMER_MANAGEMENT_ACTION_TYPE.clearAcceptedLatestTermsAndConditions,
		};
	}

	acceptTermsAndConditionsFail(payload: AppError): ActionWithPayload {
		return {
			type: CUSTOMER_MANAGEMENT_ACTION_TYPE.acceptTermsAndConditionsFail,
			payload,
		};
	}

	dismissAcceptTermsAndConditions(): Action {
		return {
			type: CUSTOMER_MANAGEMENT_ACTION_TYPE.dismissAcceptTermsAndConditions,
		};
	}

	confirmProfile(
		requestData: ConfirmPegaCustomerProfileRequest | ConfirmCogaCustomerProfileRequest
	): ActionWithPayload {
		return {
			type: CUSTOMER_MANAGEMENT_ACTION_TYPE.confirmProfile,
			payload: requestData,
		};
	}

	ConfirmProfileSuccess(): Action {
		return {
			type: CUSTOMER_MANAGEMENT_ACTION_TYPE.confirmProfileSuccess,
		};
	}

	ConfirmProfileFail(error: AppError): ActionWithPayload<AppError> {
		return {
			type: CUSTOMER_MANAGEMENT_ACTION_TYPE.confirmProfileFail,
			payload: error,
		};
	}

	clearDismissTermsAndConditions(): Action {
		return {
			type: CUSTOMER_MANAGEMENT_ACTION_TYPE.clearDismissedTermsAndConditions,
		};
	}

	latestPrivacyPolicyNotAccepted(payload: AppError): ActionWithPayload {
		return {
			type: CUSTOMER_MANAGEMENT_ACTION_TYPE.latestPrivacyPolicyNotAccepted,
			payload,
		};
	}

	entityLegalNoticeNotAccepted(payload: AppError): ActionWithPayload {
		return {
			type: CUSTOMER_MANAGEMENT_ACTION_TYPE.entityLegalNoticeNotAccepted,
			payload,
		};
	}

	acceptPrivacyPolicy(token: string): ActionWithPayload {
		return {
			type: CUSTOMER_MANAGEMENT_ACTION_TYPE.acceptPrivacyPolicy,
			payload: token,
		};
	}

	acceptEntityLegalNotice(token: string): ActionWithPayload {
		return {
			type: CUSTOMER_MANAGEMENT_ACTION_TYPE.acceptEntityLegalNotice,
			payload: token,
		};
	}

	acceptEntityLegalNoticeSuccess(): Action {
		return {
			type: CUSTOMER_MANAGEMENT_ACTION_TYPE.acceptEntityLegalNoticeSuccess,
		};
	}

	acceptEntityLegalNoticeFail(payload: AppError): ActionWithPayload {
		return {
			type: CUSTOMER_MANAGEMENT_ACTION_TYPE.acceptEntityLegalNoticeFail,
			payload,
		};
	}

	acceptPrivacyPolicySuccess(): Action {
		return {
			type: CUSTOMER_MANAGEMENT_ACTION_TYPE.acceptPrivacyPolicySuccess,
		};
	}

	clearAcceptedLatestPrivacyPolicy(): Action {
		return {
			type: CUSTOMER_MANAGEMENT_ACTION_TYPE.clearAcceptedLatestPrivacyPolicy,
		};
	}

	acceptPrivacyPolicyFail(payload: AppError): ActionWithPayload {
		return {
			type: CUSTOMER_MANAGEMENT_ACTION_TYPE.acceptPrivacyPolicyFail,
			payload,
		};
	}

	activateDepositTermsAndConditions(): Action {
		return {
			type: CUSTOMER_MANAGEMENT_ACTION_TYPE.activateDepositTermsAndConditions,
		};
	}

	cancelDepositTermsAndConditions(): Action {
		return {
			type: CUSTOMER_MANAGEMENT_ACTION_TYPE.cancelDepositTermsAndConditions,
		};
	}

	acceptDepositTermsAndConditions(): Action {
		return {
			type: CUSTOMER_MANAGEMENT_ACTION_TYPE.acceptDepositTermsAndConditions,
		};
	}

	acceptDepositTermsAndConditionsSuccess(payload: Partial<Customer>): ActionWithPayload {
		return {
			type: CUSTOMER_MANAGEMENT_ACTION_TYPE.acceptDepositTermsAndConditionsSuccess,
			payload,
		};
	}

	acceptDepositTermsAndConditionsFail(payload: AppError): ActionWithPayload {
		return {
			type: CUSTOMER_MANAGEMENT_ACTION_TYPE.acceptDepositTermsAndConditionsFail,
			payload,
		};
	}

	clearCustomer(preserveCustomerEmail = false): ActionWithPayload {
		return {
			type: CUSTOMER_MANAGEMENT_ACTION_TYPE.clearCustomer,
			payload: preserveCustomerEmail,
		};
	}

	declineTermsAndConditions(token: string): ActionWithPayload {
		return {
			type: CUSTOMER_MANAGEMENT_ACTION_TYPE.declineTermsAndConditions,
			payload: token,
		};
	}

	declineTermsAndConditionsSuccess(): Action {
		return {
			type: CUSTOMER_MANAGEMENT_ACTION_TYPE.declineTermsAndConditionsSuccess,
		};
	}

	declineTermsAndConditionsFail(payload: AppError): ActionWithPayload {
		return {
			type: CUSTOMER_MANAGEMENT_ACTION_TYPE.declineTermsAndConditionsFail,
			payload,
		};
	}

	updateCredentials(payload: LoginRequest): ActionWithPayload {
		return {
			type: CUSTOMER_MANAGEMENT_ACTION_TYPE.updateCredentials,
			payload,
		};
	}

	deleteCredentials(): Action {
		return {
			type: CUSTOMER_MANAGEMENT_ACTION_TYPE.deleteCredentials,
		};
	}

	updateCustomerCredentialTypes(payload: CustomerCredentialType): ActionWithPayload {
		return {
			type: CUSTOMER_MANAGEMENT_ACTION_TYPE.updateCustomerCredentialTypes,
			payload,
		};
	}

	updatePersistentData(key: string, value: any): ActionWithPayload {
		return {
			type: CUSTOMER_MANAGEMENT_ACTION_TYPE.updatePersistentData,
			payload: { [key]: value } as Record<string, any>,
		};
	}

	updateProfileValidation(): Action {
		return {
			type: CUSTOMER_MANAGEMENT_ACTION_TYPE.updateProfileValidation,
		};
	}

	updateProfileValidationSuccess(): Action {
		return {
			type: CUSTOMER_MANAGEMENT_ACTION_TYPE.updateProfileValidationSuccess,
		};
	}

	updateProfileValidationFail(payload: AppError): ActionWithPayload {
		return {
			type: CUSTOMER_MANAGEMENT_ACTION_TYPE.updateProfileValidationFail,
			payload,
		};
	}
}
